'use client';

import { useEffect } from 'react';
import { setupDataWidths } from './setup-data-widths';
import { setupIndustriesSlider } from './setup-industries-slider';
import { setupHomeNavigation } from './setup-navigation';
import { setupLazyMedia } from './setup-lazy-media';

export function HomeEffects() {
  useEffect(() => {
    const root = document.querySelector<HTMLElement>('.light-ship-smart-root');
    const wrapper = root?.querySelector<HTMLElement>('#smooth-wrapper');
    const content = root?.querySelector<HTMLElement>('#smooth-content');
    if (!root || !wrapper || !content) return;
    const homeRoot = root;
    const smoothWrapper = wrapper;
    const smoothContent = content;

    let cancelled = false;
    let effectsStarted = false;
    let cleanupHeavyEffects = () => {};
    const immediateCleanups = [
      setupDataWidths(homeRoot),
      setupIndustriesSlider(homeRoot),
      setupHomeNavigation(homeRoot, null),
      setupLazyMedia(homeRoot),
    ];

    const year = homeRoot.querySelector<HTMLElement>('.update-year');
    if (year) year.textContent = String(new Date().getFullYear());

    const interactionEvents = [
      'pointermove',
      'touchstart',
      'wheel',
      'keydown',
    ] as const;

    const removeInteractionListeners = () => {
      interactionEvents.forEach((eventName) => {
        window.removeEventListener(eventName, initializeHeavyEffects);
      });
    };

    async function initializeHeavyEffects() {
      if (effectsStarted) return;
      effectsStarted = true;
      removeInteractionListeners();

      const [
        { gsap },
        { ScrollSmoother },
        { ScrollToPlugin },
        { ScrollTrigger },
        { SplitText },
        { setupHomeMotion },
        { setupMagicCursor },
        { setupHomeSliders },
      ] = await Promise.all([
        import('gsap'),
        import('gsap/ScrollSmoother'),
        import('gsap/ScrollToPlugin'),
        import('gsap/ScrollTrigger'),
        import('gsap/SplitText'),
        import('./setup-home-motion'),
        import('./setup-magic-cursor'),
        import('./setup-home-sliders'),
      ]);
      if (cancelled) return;

      gsap.registerPlugin(
        ScrollTrigger,
        ScrollSmoother,
        ScrollToPlugin,
        SplitText
      );
      ScrollSmoother.get()?.kill();

      const isMobile = window.matchMedia('(max-width: 767.98px)').matches;
      const smoother = isMobile
        ? null
        : ScrollSmoother.create({
            wrapper: smoothWrapper,
            content: smoothContent,
            smooth: 1.35,
            effects: true,
            smoothTouch: 0.15,
            ignoreMobileResize: true,
          });
      const cleanups = [
        setupMagicCursor(homeRoot),
        setupHomeSliders(homeRoot),
        setupHomeMotion(homeRoot),
      ];

      let refreshFrame: number | undefined;
      const settleFrame = window.requestAnimationFrame(() => {
        refreshFrame = window.requestAnimationFrame(() => {
          ScrollTrigger.refresh();
        });
      });

      cleanupHeavyEffects = () => {
        window.cancelAnimationFrame(settleFrame);
        if (refreshFrame !== undefined) {
          window.cancelAnimationFrame(refreshFrame);
        }
        cleanups.reverse().forEach((cleanup) => cleanup());
        smoother?.kill();
        ScrollTrigger.clearScrollMemory();
      };
    }

    interactionEvents.forEach((eventName) => {
      window.addEventListener(eventName, initializeHeavyEffects, {
        once: true,
        passive: true,
      });
    });

    return () => {
      cancelled = true;
      removeInteractionListeners();
      cleanupHeavyEffects();
      immediateCleanups.reverse().forEach((cleanup) => cleanup());
    };
  }, []);

  return null;
}
